import { Typography } from '@mui/material'
import { A2UIComponent } from 'pbts/c1/api/a2ui/v1/components'
import { FC, ReactNode, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import {
    ButtonRenderer,
    C1CodeBlockRenderer,
    C1StatusIndicatorRenderer,
    CardRenderer,
    CheckBoxRenderer,
    ChoicePickerRenderer,
    ColumnRenderer,
    DividerRenderer,
    RowRenderer,
    TextFieldRenderer,
    TextRenderer,
} from './renderers'

type ComponentRendererProps = {
    component: A2UIComponent
    componentsMap: Map<string, A2UIComponent>
    dataModel: Record<string, unknown>
    onDataChange: (path: string, value: unknown) => void
    onAction: (actionName: string, context: Record<string, string>, sourceComponentId: string) => void
    disabled?: boolean
    loadingComponents?: Set<string>
}

/**
 * Main renderer that delegates to individual component renderers based on type.
 * Each component type has its own renderer file in the renderers/ directory.
 */
export const A2UIComponentRenderer: FC<ComponentRendererProps> = ({
    component,
    componentsMap,
    dataModel,
    onDataChange,
    onAction,
    disabled,
    loadingComponents,
}) => {
    const renderChildren = useCallback(
        (childIds: string[]): ReactNode =>
            childIds.map((id) => {
                const child = componentsMap.get(id)
                if (!child) return null
                return (
                    <A2UIComponentRenderer
                        key={id}
                        component={child}
                        componentsMap={componentsMap}
                        dataModel={dataModel}
                        onDataChange={onDataChange}
                        onAction={onAction}
                        disabled={disabled}
                        loadingComponents={loadingComponents}
                    />
                )
            }),
        [componentsMap, dataModel, onDataChange, onAction, disabled, loadingComponents],
    )

    switch (component.component.oneofKind) {
        case 'text':
            return <TextRenderer component={component} dataModel={dataModel} />

        case 'textField':
            return (
                <TextFieldRenderer
                    component={component}
                    dataModel={dataModel}
                    onDataChange={onDataChange}
                    disabled={disabled}
                />
            )

        case 'checkBox':
            return (
                <CheckBoxRenderer
                    component={component}
                    dataModel={dataModel}
                    onDataChange={onDataChange}
                    disabled={disabled}
                />
            )

        case 'choicePicker':
            return (
                <ChoicePickerRenderer
                    component={component}
                    dataModel={dataModel}
                    onDataChange={onDataChange}
                    disabled={disabled}
                />
            )

        case 'button':
            return (
                <ButtonRenderer
                    component={component}
                    dataModel={dataModel}
                    onAction={onAction}
                    disabled={disabled}
                    loadingComponents={loadingComponents}
                />
            )

        case 'row':
            return (
                <RowRenderer
                    component={component}
                    dataModel={dataModel}
                    renderChildren={renderChildren}
                />
            )

        case 'column':
            return (
                <ColumnRenderer
                    component={component}
                    dataModel={dataModel}
                    renderChildren={renderChildren}
                />
            )

        case 'card':
            return <CardRenderer component={component} renderChildren={renderChildren} />

        case 'divider':
            return <DividerRenderer component={component} />

        case 'c1StatusIndicator':
            return <C1StatusIndicatorRenderer component={component} dataModel={dataModel} />

        case 'c1CodeBlock':
            return <C1CodeBlockRenderer component={component} dataModel={dataModel} />

        default:
            return (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    <FormattedMessage
                        id="a2ui_unsupported_component"
                        defaultMessage="[Unsupported component: {kind}]"
                        values={{ kind: component.component.oneofKind ?? 'unknown' }}
                    />
                </Typography>
            )
    }
}
